import { useState } from 'react'
import {
  CompanyLogo,
  IntegratedAccountPill,
  useSEOCountriesJSON,
} from '@pepper/ui'
import { ContentRefreshDomain } from '@pepper/utils/src/types/domain'
import clsx from 'clsx'

import { extractHostName } from 'utils/functions/extractHostName'
import { getAutoMatchProperties } from 'utils/functions/domain'
import GoogleAccountPropertySelector from 'components/GoogleAccountPropertySelector'
import { useGscValidation } from 'utils/api/useGscValidation'
import { useGaValidation } from 'utils/api/useGaValidation'

import s from './Domain.module.css'

type Option = {
  label: {
    header: string
  }
  value: string
}

type Props = {
  domain: ContentRefreshDomain
  googleProperties: {
    gaOptions: Option[]
    gscOptions: Option[]
  }
  companyId: string
  onChange: (id: string, type: 'gsc' | 'ga', value?: string) => void
}

export const Domain = ({
  domain,
  googleProperties: { gaOptions, gscOptions },
  companyId,
  onChange,
}: Props) => {
  const { data: countriesJson } = useSEOCountriesJSON()
  const [selectedGaProperty, setSelectedGaProperty] = useState<
    string | undefined
  >(domain.ga_property)
  const [selectedGscProperty, setSelectedGscProperty] = useState<
    string | undefined
  >(domain.gsc_domain_property)

  const country = countriesJson?.[domain.country_code]?.value

  const { data: gscValidation, isLoading: isValidatingGsc } = useGscValidation({
    companyId,
    uri: selectedGscProperty,
    country,
    enabled: !!country && !!selectedGscProperty,
  })

  const { data: gaValidation, isLoading: isValidatingGa } = useGaValidation({
    companyId,
    uriId: selectedGaProperty,
    country,
    enabled: !!country && !!selectedGaProperty,
  })

  const { gaExactMatch, gscExactMatch, hasGaAccess, hasGscAccess } =
    getAutoMatchProperties({
      domain: domain.domain_url,
      gaProperties: gaOptions,
      gscProperties: gscOptions,
    })

  const hasAutoMatch = !!gscExactMatch && !!gaExactMatch
  const hasGaOrGscAccess = hasGaAccess || hasGscAccess
  const areBothSelected =
    hasAutoMatch || (!!domain.ga_property && !!domain.gsc_domain_property)

  const handleChange = (type: 'ga' | 'gsc', value?: string) => {
    if (type === 'ga') {
      setSelectedGaProperty(value)
    } else {
      setSelectedGscProperty(value)
    }
    onChange(domain.id, type, value)
  }

  const getDescription = () => {
    if (hasGaOrGscAccess) return 'Match Properties'

    return 'No GA/GSC Access'
  }

  return (
    <div className={s.wrapper}>
      <div
        className={clsx(s.domainWrapper, {
          [s.noAutoMatch]: !areBothSelected && hasGaOrGscAccess,
        })}
      >
        <div className={s.domain}>
          <CompanyLogo
            companyName={extractHostName(domain.domain_url)}
            className={s.logo}
          />
          {domain.domain_url}
          <img
            className={s.flag}
            src={countriesJson?.[domain.country_code]?.flag}
            alt={domain.country_code}
          />
          <span className={s.country}>
            {countriesJson?.[domain.country_code]?.label}
          </span>
        </div>
        <div className={s.autoMatchStatus}>
          {!areBothSelected && (
            <span className={s.description}>{getDescription()}</span>
          )}
          <IntegratedAccountPill
            isGoogleConnected={true}
            isIntegrationValid={areBothSelected}
          />
        </div>
      </div>

      {!areBothSelected && hasGaOrGscAccess && (
        <div className={s.propertySelectorWrapper}>
          <GoogleAccountPropertySelector
            title="Google Search Console"
            name="gsc"
            onChange={value => {
              if (value && !domain.gsc_domain_property) {
                handleChange('gsc', value)
              }
            }}
            options={gscOptions}
            value={domain.gsc_domain_property || gscExactMatch}
            isValid={gscValidation?.data || isValidatingGsc}
            disabled={!!domain.gsc_domain_property}
            allowClear={false}
          />
          <GoogleAccountPropertySelector
            title="Google Analytics"
            name="ga"
            onChange={value => {
              if (value && !domain.ga_property) {
                handleChange('ga', value?.split('_')[0])
              }
            }}
            options={gaOptions}
            value={domain.ga_property || gaExactMatch}
            isValid={gaValidation?.data || isValidatingGa}
            disabled={!!domain.ga_property}
            allowClear={false}
          />
        </div>
      )}
    </div>
  )
}
